import { useEffect, useRef, useState } from "react";
import { X, Volume2, VolumeX, Maximize2, Play } from "lucide-react";

const VIDEO_BASE = process.env.PUBLIC_URL + "/marketing/demo";
const STORAGE_KEY = "shansoai_demo_popup_v1";
const AUTO_OPEN_DELAY_MS = 4000;

/**
 * Floating bottom-right picture-in-picture demo popup for the landing page.
 * - Auto-opens 4s after mount, once per session.
 * - Starts muted to satisfy browser autoplay; click anywhere on the player to unmute.
 * - Dismissible (X), expandable to centered modal (Maximize2), and persistent dismissal in session.
 */
export default function LandingDemoPopup() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [showUnmuteHint, setShowUnmuteHint] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    // Only auto-open once per session
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "dismissed") return;
    } catch { /* sessionStorage blocked — proceed anyway */ }

    const t = setTimeout(() => setVisible(true), AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible || !videoRef.current) return;
    // Attempt to autoplay; ignore promise rejection (browsers may block)
    videoRef.current.play().catch(() => { /* no-op */ });
  }, [visible]);

  useEffect(() => {
    // Hide the "tap to unmute" hint after 5s
    if (!visible || !showUnmuteHint) return;
    const t = setTimeout(() => setShowUnmuteHint(false), 5000);
    return () => clearTimeout(t);
  }, [visible, showUnmuteHint]);

  function dismiss() {
    setVisible(false);
    try { sessionStorage.setItem(STORAGE_KEY, "dismissed"); } catch { /* ignore */ }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }

  function toggleMute(e) {
    e?.stopPropagation();
    setMuted((m) => !m);
    setShowUnmuteHint(false);
  }

  function onPlayerClick() {
    if (muted) {
      setMuted(false);
      setShowUnmuteHint(false);
    }
  }

  function toggleExpand(e) {
    e?.stopPropagation();
    setExpanded((x) => !x);
  }

  if (!visible) return null;

  const wrapperClass = expanded
    ? "fixed inset-0 z-[220] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
    : "fixed bottom-5 right-5 z-[210] w-[360px] max-w-[calc(100vw-2.5rem)]";

  const cardClass = expanded
    ? "w-full max-w-[960px] aspect-video bg-black rounded-xl border border-zinc-800 overflow-hidden shadow-2xl relative"
    : "aspect-video bg-black rounded-xl border border-zinc-700 overflow-hidden shadow-2xl shadow-cyan-500/10 relative ring-1 ring-cyan-400/20";

  return (
    <div
      className={wrapperClass}
      onClick={expanded ? dismiss : undefined}
      data-testid="demo-popup"
    >
      <div className={cardClass} onClick={(e) => e.stopPropagation()}>
        {/* Top control bar */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-2.5 py-1.5 bg-gradient-to-b from-black/70 to-transparent">
          <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            Demo · 75 sec
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleMute}
              className="p-1 rounded hover:bg-white/10 text-zinc-200"
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="demo-mute-btn"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <button
              onClick={toggleExpand}
              className="p-1 rounded hover:bg-white/10 text-zinc-200"
              aria-label={expanded ? "Collapse" : "Expand"}
              data-testid="demo-expand-btn"
            >
              <Maximize2 size={14} />
            </button>
            <button
              onClick={dismiss}
              className="p-1 rounded hover:bg-white/10 text-zinc-200"
              aria-label="Close demo"
              data-testid="demo-close-btn"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Video (captions are burned into the video frames) */}
        <video
          ref={videoRef}
          autoPlay
          muted={muted}
          playsInline
          preload="auto"
          onClick={onPlayerClick}
          onEnded={dismiss}
          className="w-full h-full object-cover cursor-pointer"
          data-testid="demo-video"
        >
          <source src={`${VIDEO_BASE}.webm`} type="video/webm" />
          <source src={`${VIDEO_BASE}.mp4`} type="video/mp4" />
        </video>

        {/* Tap-to-unmute hint */}
        {muted && showUnmuteHint && (
          <button
            onClick={toggleMute}
            className="absolute inset-x-0 bottom-3 mx-auto w-fit flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/80 border border-cyan-400/40 text-cyan-200 text-[11px] font-medium hover:bg-black animate-pulse"
            data-testid="demo-unmute-hint"
          >
            <Play size={11} /> Tap to unmute
          </button>
        )}
      </div>
    </div>
  );
}
